import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import TopExperienceLayer from '../components/top/TopExperienceLayer';
import Footer from '../components/layout/Footer';
import LeadCaptureModal from '../components/LeadCaptureModal';
import { collegesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import CircleGauge from '../components/charts/CircleGauge';
import HorizontalBarChart from '../components/charts/HorizontalBarChart';
import StatCard from '../components/charts/StatCard';
import ValueComparisonBar from '../components/charts/ValueComparisonBar';
import { 
  formatCurrency, 
  formatLocation,
  getBadgeColors 
} from '../utils/formatters';
import {
  getCollegeLogo,
  handleCampusImgError,
} from '../utils/imagekit';
import {
  MapPin,
  GraduationCap,
  DollarSign,
  Users,
  Heart,
  Share2,
  ExternalLink,
  Award,
  BookOpen,
  Building2,
  CheckCircle,
  TrendingUp,
  Menu,
  Video,
  Calendar,
  MessageSquare,
  Instagram,
  Facebook
} from 'lucide-react';

const CollegeSeal = ({ college }) => {
  const slug = college?.identity?.slug;
  const logoUrl = college?.images?.logoUrl || getCollegeLogo(slug);
  const initial = (college?.identity?.name || 'C').substring(0, 1).toUpperCase();
  const [logoBroken, setLogoBroken] = useState(false);
  const showLogo = Boolean(logoUrl) && !logoBroken;

  return (
    <div className="w-[80px] h-[80px] rounded-md bg-[#1a5d3a] border border-gray-200 flex-shrink-0 flex items-center justify-center overflow-hidden">
      {showLogo ? (
        <img
          src={logoUrl}
          alt={`${college?.identity?.name || 'College'} logo`}
          className="w-full h-full object-contain p-1"
          onError={() => setLogoBroken(true)}
        />
      ) : (
        <span className="text-2xl font-bold text-white" aria-label={`${college?.identity?.name || 'College'} initial`}>
          {initial}
        </span>
      )}
    </div>
  );
};

const CollegeDetailPageEnhanced = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [savingInFlight, setSavingInFlight] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [showMobileNav, setShowMobileNav] = useState(false);

  const sectionRefs = {
    overview: useRef(null),
    admissions: useRef(null),
    costs: useRef(null),
    outcomes: useRef(null),
    studentBody: useRef(null),
    programs: useRef(null)
  };

  const handleShare = async () => {
    const shareData = {
      title: college?.identity?.name,
      text: `Check out ${college?.identity?.name} on Student Signal`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    } catch (_clipboardError) {
      const url = window.location.href;
      prompt('Copy this link:', url);
    }
  };

  useEffect(() => {
    const fetchCollege = async () => {
      try {
        setLoading(true);
        const data = await collegesAPI.getCollege(id);
        setCollege(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching college:', err);
        setError('Failed to load college details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCollege();
  }, [id]);

  // Check whether this college is already in the user's saved list (auth-only)
  useEffect(() => {
    if (!isAuthenticated || !id) {
      setIsSaved(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const saved = await collegesAPI.getSavedColleges();
        if (!active) return;
        const slugs = Array.isArray(saved)
          ? saved.map((c) => c?.slug || c?.ipedsId).filter(Boolean)
          : [];
        setIsSaved(slugs.includes(id));
      } catch (err) {
        // Non-fatal: leave isSaved as false
        console.error('Failed to load saved colleges:', err);
      }
    })();
    return () => { active = false; };
  }, [id, isAuthenticated]);

  const handleToggleSave = async () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    const slug = college?.identity?.slug || id;
    if (!slug || savingInFlight) return;

    setSavingInFlight(true);
    try {
      if (isSaved) {
        await collegesAPI.unsaveCollege(slug);
        setIsSaved(false);
        toast.success('Removed from saved colleges');
      } else {
        await collegesAPI.saveCollege(slug);
        setIsSaved(true);
        toast.success('Saved to your colleges');
      }
    } catch (err) {
      console.error('Failed to update saved status:', err);
      const detail = err?.response?.data?.detail || 'Could not update saved status. Please try again.';
      toast.error(detail);
    } finally {
      setSavingInFlight(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      Object.entries(sectionRefs).forEach(([key, ref]) => {
        if (ref.current) {
          const { offsetTop, offsetHeight } = ref.current;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(key);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  // eslint-disable-next-line
  }, []);

  const scrollToSection = (sectionKey) => {
    sectionRefs[sectionKey]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setShowMobileNav(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <TopExperienceLayer />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#1a5d3a] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading college details...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !college) {
    return (
      <div className="min-h-screen flex flex-col">
        <TopExperienceLayer />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {error || 'College not found'}
            </h1>
            <Link to="/colleges">
              <Button className="bg-[#1a5d3a] hover:bg-[#2d8659]">Browse Colleges</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const navItems = [
    { key: 'overview', label: 'Overview', icon: Building2 },
    { key: 'admissions', label: 'Admissions', icon: GraduationCap },
    { key: 'costs', label: 'Cost & Aid', icon: DollarSign },
    { key: 'outcomes', label: 'Outcomes', icon: TrendingUp },
    { key: 'studentBody', label: 'Student Body', icon: Users },
    { key: 'programs', label: 'Programs', icon: BookOpen }
  ];

  return (
    <div className="w-full overflow-x-hidden bg-gray-50">
      <div className="min-h-screen flex flex-col">
        <TopExperienceLayer />
        
        <main className="flex-grow">
          {/* Hero Section — original container; SVG fallback uses `contain` on brand-green bg, real photos `cover`. */}
          {(() => {
            const heroSrc =
              college.images?.bannerUrl
              || college.images?.heroUrl
              || '/assets/fallback-campus-hero.svg';
            const isSvg = /\.svg($|\?)/i.test(heroSrc);
            return (
              <div
                className="w-full overflow-hidden relative h-[300px] md:h-[400px]"
                style={isSvg ? { backgroundColor: '#2d7a52' } : undefined}
              >
                <img
                  src={heroSrc}
                  alt={`${college.identity?.name} campus`}
                  className="block w-full h-full"
                  style={{
                    objectFit: isSvg ? 'contain' : 'cover',
                    objectPosition: 'center',
                  }}
                  onError={handleCampusImgError}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none"></div>
              </div>
            );
          })()}

          {/* White Content Zone Below Hero */}
          <div className="w-full bg-white border-b border-gray-200">
            <div className="w-full max-w-7xl mx-auto px-4 pt-6 pb-6">
              {/* Row 1: Top Action Bar */}
              <div className="flex justify-end mb-6">
                <div className="flex flex-col gap-2 w-full md:flex-row md:w-auto md:gap-2 md:flex-shrink-0">
                  {college.contact?.youtube && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full md:w-auto flex-shrink-0 border-gray-300 text-gray-700 hover:bg-gray-50 justify-center h-8 px-3 text-xs"
                      onClick={() => window.open(
                        college.contact?.youtube || college.contact?.website,
                        '_blank'
                      )}
                      data-testid="video-btn"
                    >
                      <Video size={12} className="mr-1" />
                      Video
                    </Button>
                  )}
                  {college.contact?.instagram && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full md:w-auto flex-shrink-0 border-gray-300 text-gray-700 hover:bg-gray-50 justify-center h-8 px-3 text-xs"
                      onClick={() => window.open(college.contact.instagram, '_blank')}
                      title="Instagram"
                      data-testid="instagram-btn"
                    >
                      <Instagram size={12} className="mr-1" />
                      Instagram
                    </Button>
                  )}
                  {college.contact?.facebook && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full md:w-auto flex-shrink-0 border-gray-300 text-gray-700 hover:bg-gray-50 justify-center h-8 px-3 text-xs"
                      onClick={() => window.open(college.contact.facebook, '_blank')}
                      title="Facebook"
                      data-testid="facebook-btn"
                    >
                      <Facebook size={12} className="mr-1" />
                      Facebook
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full md:w-auto flex-shrink-0 border-gray-300 text-gray-700 hover:bg-gray-50 justify-center h-8 px-3 text-xs"
                    onClick={() => window.location.href = `/colleges/${id}/events`}
                  >
                    <Calendar size={12} className="mr-1" />
                    Events
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full md:w-auto flex-shrink-0 border-gray-300 text-gray-700 hover:bg-gray-50 justify-center h-8 px-3 text-xs"
                    onClick={() => window.location.href = `/colleges/${id}/reviews`}
                  >
                    <MessageSquare size={12} className="mr-1" />
                    Reviews
                  </Button>
                  {college.contact?.website && (
                    <Button 
                      size="sm" 
                      className="w-full md:w-auto flex-shrink-0 bg-[#f5a623] hover:bg-[#e09000] text-white justify-center h-8 px-3 text-xs"
                      onClick={() => window.open(college.contact.website, '_blank')}
                    >
                      <ExternalLink size={12} className="mr-1" />
                      Website
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full md:w-auto flex-shrink-0 border-gray-300 text-gray-700 hover:bg-gray-50 justify-center h-8 px-3"
                    onClick={handleShare}
                    title="Share"
                  >
                    <Share2 size={12} />
                    <span className="md:hidden ml-2 text-xs">Share</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={savingInFlight}
                    className="w-full md:w-auto flex-shrink-0 border-gray-300 text-gray-700 hover:bg-gray-50 justify-center h-8 px-3 disabled:opacity-60"
                    onClick={handleToggleSave}
                    title={isSaved ? 'Saved' : 'Save'}
                    data-testid="save-college-btn"
                  >
                    <Heart size={12} className={isSaved ? 'fill-red-500 text-red-500' : ''} />
                    <span className="md:hidden ml-2 text-xs">{isSaved ? 'Saved' : 'Save'}</span>
                  </Button>
                </div>
              </div>

              {/* Row 2: College Name + Logo */}
              <div className="flex gap-4">
                {/* Seal Box — real logo if available, initials fallback otherwise */}
                <CollegeSeal college={college} />

                {/* College Name + Location + Pills */}
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-2">
                    {college.identity?.name}
                  </h1>
                  
                  {/* Location + Pills */}
                  <div className="flex flex-wrap items-center gap-2 text-gray-600 text-sm">
                    <MapPin size={16} className="flex-shrink-0" />
                    <span>{formatLocation(college.location?.city, college.location?.stateCode)}</span>
                    {college.identity?.control && (
                      <span className={`${getBadgeColors(college.identity.control).bg} ${getBadgeColors(college.identity.control).text} text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap`}>
                        {college.identity.control === '1' ? 'Public' : college.identity.control === '2' ? 'Private' : 'Private For-Profit'}
                      </span>
                    )}
                    {college.identity?.level && (
                      <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                        {college.identity.level === '1' ? '4-Year' : '2-Year'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full max-w-7xl mx-auto px-4 py-8">
            <div className="flex gap-8">
              {/* Left Navigation - Desktop */}
              <aside className="hidden lg:block w-64 flex-shrink-0" style={{ position: 'sticky', top: '6rem', alignSelf: 'flex-start' }}>
                <nav className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => scrollToSection(item.key)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeSection === item.key
                            ? 'bg-emerald-50 text-emerald-700 font-semibold'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon size={18} />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </aside>

              {/* Mobile Navigation Button */}
              <button
                onClick={() => setShowMobileNav(!showMobileNav)}
                className="lg:hidden fixed bottom-6 right-6 z-50 bg-emerald-600 text-white p-4 rounded-full shadow-lg"
              >
                <Menu size={24} />
              </button>

              {/* Mobile Navigation Overlay */}
              {showMobileNav && (
                <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setShowMobileNav(false)}>
                  <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-6" onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-lg font-bold mb-4">Jump to Section</h3>
                    <div className="space-y-2">
                      {navItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.key}
                            onClick={() => scrollToSection(item.key)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left ${
                              activeSection === item.key
                                ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Icon size={18} />
                            <span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content Area */}
              <div className="flex-1 space-y-8">
                {/* Overview Section */}
                <section ref={sectionRefs.overview} id="overview" className="scroll-mt-24">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      {college.identity?.name} is a {college.identity?.control === '1' ? 'public' : 'private'} {college.identity?.level === '1' ? '4-year' : '2-year'} institution located in {formatLocation(college.location?.city, college.location?.stateCode)}.
                      {college.admissions?.acceptanceRate && ` With an acceptance rate of ${Math.round(college.admissions.acceptanceRate * 100)}%, `}
                      the institution offers comprehensive academic programs.
                      {college.enrollment?.totalEnrollment && ` The college serves ${college.enrollment.totalEnrollment.toLocaleString()} students.`}
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {college.admissions?.acceptanceRate && (
                        <StatCard
                          icon={CheckCircle}
                          label="Acceptance Rate"
                          value={`${Math.round(college.admissions.acceptanceRate * 100)}%`}
                        />
                      )}
                      {college.costs?.tuitionInState && (
                        <StatCard
                          icon={DollarSign}
                          label="In-State Tuition"
                          value={formatCurrency(college.costs.tuitionInState)}
                        />
                      )}
                      {college.admissions?.satMidpoint && (
                        <StatCard
                          icon={Award}
                          label="SAT Average"
                          value={college.admissions.satMidpoint}
                        />
                      )}
                      {college.enrollment?.totalEnrollment && (
                        <StatCard
                          icon={Users}
                          label="Total Students"
                          value={college.enrollment.totalEnrollment.toLocaleString()}
                        />
                      )}
                    </div>
                  </div>
                </section>

                {/* Admissions Section */}
                <section ref={sectionRefs.admissions} id="admissions" className="scroll-mt-24">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Admissions</h2>
                    
                    <div className="grid md:grid-cols-3 gap-8 mb-8">
                      {college.admissions?.acceptanceRate && (
                        <div className="flex flex-col items-center">
                          <CircleGauge
                            value={Math.round(college.admissions.acceptanceRate * 100)}
                            label="Acceptance Rate"
                            color="#1a5d3a"
                          />
                        </div>
                      )}
                      
                      {college.admissions?.satMidpoint && (
                        <div className="flex flex-col items-center justify-center">
                          <div className="text-center">
                            <p className="text-4xl font-bold text-gray-900 mb-2">
                              {college.admissions.satMidpoint}
                            </p>
                            <p className="text-sm text-gray-600">SAT Midpoint</p>
                          </div>
                        </div>
                      )}
                      
                      {college.admissions?.actMidpoint && (
                        <div className="flex flex-col items-center justify-center">
                          <div className="text-center">
                            <p className="text-4xl font-bold text-gray-900 mb-2">
                              {college.admissions.actMidpoint}
                            </p>
                            <p className="text-sm text-gray-600">ACT Midpoint</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {/* Cost & Aid Section */}
                <section ref={sectionRefs.costs} id="costs" className="scroll-mt-24">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Cost & Aid</h2>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      {college.costs?.tuitionInState && (
                        <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                          <p className="text-sm text-gray-600 mb-2">In-State Tuition</p>
                          <p className="text-3xl font-bold text-emerald-700">
                            {formatCurrency(college.costs.tuitionInState)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">per year</p>
                        </div>
                      )}
                      
                      {college.costs?.tuitionOutOfState && (
                        <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                          <p className="text-sm text-gray-600 mb-2">Out-of-State Tuition</p>
                          <p className="text-3xl font-bold text-orange-600">
                            {formatCurrency(college.costs.tuitionOutOfState)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">per year</p>
                        </div>
                      )}
                    </div>

                    {(college.costs?.tuitionInState || college.costs?.avgNetPriceOverall) && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold mb-4">Cost Comparison</h3>
                        <ValueComparisonBar
                          values={[
                            { 
                              label: 'In-State Tuition', 
                              value: college.costs?.tuitionInState, 
                              color: '#1a5d3a' 
                            },
                            { 
                              label: 'Out-of-State Tuition', 
                              value: college.costs?.tuitionOutOfState, 
                              color: '#f5a623' 
                            },
                            { 
                              label: 'Average Net Price', 
                              value: college.costs?.avgNetPriceOverall, 
                              color: '#3b82f6' 
                            }
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </section>

                {/* Outcomes Section */}
                <section ref={sectionRefs.outcomes} id="outcomes" className="scroll-mt-24">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Outcomes</h2>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                      {college.outcomes?.graduationRate6yr && (
                        <div className="flex flex-col items-center">
                          <CircleGauge
                            value={Math.round(college.outcomes.graduationRate6yr * 100)}
                            label="6-Year Graduation Rate"
                            color="#3b82f6"
                          />
                        </div>
                      )}
                      
                      {college.outcomes?.graduationRate4yr && (
                        <div className="flex flex-col items-center">
                          <CircleGauge
                            value={Math.round(college.outcomes.graduationRate4yr * 100)}
                            label="4-Year Graduation Rate"
                            color="#8b5cf6"
                          />
                        </div>
                      )}
                    </div>

                    {!college.outcomes?.graduationRate6yr && !college.outcomes?.graduationRate4yr && (
                      <p className="text-gray-600 text-center py-8">
                        Graduation rate data is not currently available.
                      </p>
                    )}
                  </div>
                </section>

                {/* Student Body Section */}
                <section ref={sectionRefs.studentBody} id="studentBody" className="scroll-mt-24">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Student Body</h2>
                    
                    {college.enrollment?.totalEnrollment && (
                      <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-4">Enrollment</h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <StatCard
                            label="Total Enrollment"
                            value={college.enrollment.totalEnrollment.toLocaleString()}
                            highlight
                          />
                          {college.enrollment?.totalUndergrad && (
                            <StatCard
                              label="Undergraduates"
                              value={college.enrollment.totalUndergrad.toLocaleString()}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {college.demographics && Object.keys(college.demographics).length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Demographics</h3>
                        <HorizontalBarChart
                          data={[
                            { 
                              label: 'White', 
                              value: Math.round((college.demographics.white || 0) * 100), 
                              color: '#3b82f6' 
                            },
                            { 
                              label: 'Black/African American', 
                              value: Math.round((college.demographics.black || 0) * 100), 
                              color: '#8b5cf6' 
                            },
                            { 
                              label: 'Hispanic/Latino', 
                              value: Math.round((college.demographics.hispanic || 0) * 100), 
                              color: '#f59e0b' 
                            },
                            { 
                              label: 'Asian', 
                              value: Math.round((college.demographics.asian || 0) * 100), 
                              color: '#10b981' 
                            },
                            { 
                              label: 'Two or More Races', 
                              value: Math.round((college.demographics.twoOrMore || 0) * 100), 
                              color: '#ec4899' 
                            },
                            { 
                              label: 'International', 
                              value: Math.round((college.demographics.nonResidentAlien || 0) * 100), 
                              color: '#6366f1' 
                            }
                          ].filter(item => item.value > 0)}
                        />
                      </div>
                    )}
                  </div>
                </section>

                {/* Programs Section */}
                <section ref={sectionRefs.programs} id="programs" className="scroll-mt-24">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Programs</h2>
                    
                    {college.programs?.topMajors && college.programs.topMajors.length > 0 ? (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Top Majors</h3>
                        <ul className="space-y-2">
                          {college.programs.topMajors.map((major, index) => (
                            <li key={index} className="flex items-center gap-2">
                              <CheckCircle size={16} className="text-emerald-600" />
                              <span className="text-gray-700">{major}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-gray-600 text-center py-8">
                        Program information is not currently available. Please visit the college website for detailed academic offerings.
                      </p>
                    )}
                  </div>
                </section>

                {/* Apply CTA Section */}
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-8 text-center text-white">
                  <h3 className="text-2xl font-bold mb-4">Ready to Apply?</h3>
                  <p className="mb-6 text-white/90">
                    Take the next step in your college journey
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    {(college.contact?.applicationUrl || college.contact?.website) && (
                      <Button
                        size="lg"
                        className="bg-white text-emerald-700 hover:bg-gray-100 font-semibold"
                        onClick={() => window.open(
                          college.contact?.applicationUrl || college.contact?.website,
                          '_blank'
                        )}
                        data-testid="apply-now-btn"
                      >
                        Apply Now
                      </Button>
                    )}
                    <Button 
                      size="lg"
                      variant="outline"
                      className="border-white text-white hover:bg-white/10"
                      onClick={() => setLeadModalOpen(true)}
                    >
                      Request Information
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
        
        {college && (
          <LeadCaptureModal
            isOpen={leadModalOpen}
            onClose={() => setLeadModalOpen(false)}
            college={college}
          />
        )}

        {showLoginPrompt && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowLoginPrompt(false)}
            data-testid="login-prompt-overlay"
          >
            <div
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-2" style={{ color: '#1a5d3a' }}>
                Log in to save colleges
              </h3>
              <p className="text-gray-600 mb-6">
                Create a free account or log in to save this college to your list and track your progress.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowLoginPrompt(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium"
                  data-testid="login-prompt-cancel"
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="flex-1 px-4 py-2 text-white rounded-xl font-medium hover:opacity-90"
                  style={{ backgroundColor: '#1a5d3a' }}
                  data-testid="login-prompt-login"
                >
                  Log in
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollegeDetailPageEnhanced;
